import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const MEMBER_TIERS = new Set(["beta", "paid", "lifetime"]);

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);
  const [mispricedOpen, setMispricedOpen] = useState(false);
  const [mobileMispricedOpen, setMobileMispricedOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const isMember = !!user && MEMBER_TIERS.has(user.subscription_tier);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setToolsOpen(false);
    setMobileToolsOpen(false);
    setArticlesOpen(false);
    setMobileArticlesOpen(false);
    setMispricedOpen(false);
    setMobileMispricedOpen(false);
    setAiOpen(false);
    setMobileAiOpen(false);
  }, [location.pathname, location.search]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <nav className="navbar">
      <Link to="/" className="nav-logo">
        Nigel's
        <span>Notebook</span>
      </Link>
      <ul className="nav-links">
        <li
          className="nav-dropdown-parent"
          onMouseEnter={() => setToolsOpen(true)}
          onMouseLeave={() => setToolsOpen(false)}
        >
          <NavLink
            to="/tools/todays-card"
            title="Racecards and free analytical tools"
            className={location.pathname.startsWith("/tools/") ? "active" : ""}
          >
            Cards &amp; Tools
          </NavLink>
          {toolsOpen && (
            <ul className="nav-dropdown">
              <li><NavLink to="/tools/todays-card">Today's Cards</NavLink></li>
              <li><NavLink to="/tools/tissue">Tissue</NavLink></li>
              <li><NavLink to="/tools/trainer">Trainers</NavLink></li>
              <li><NavLink to="/tools/jockey">Jockeys</NavLink></li>
              <li><NavLink to="/tools/draw-bias">Draw Bias</NavLink></li>
              <li><NavLink to="/tools/race-radar">Race Radar</NavLink></li>
              <li><NavLink to="/tools/calculator">Calculator</NavLink></li>
            </ul>
          )}
        </li>
        <li
          className="nav-dropdown-parent"
          onMouseEnter={() => setAiOpen(true)}
          onMouseLeave={() => setAiOpen(false)}
        >
          <NavLink
            to="/previews"
            title="AI race previews and the Ask the Notebook query tool"
            className={
              location.pathname.startsWith("/previews") ||
              location.pathname.startsWith("/member/ask")
                ? "active"
                : ""
            }
          >
            AI <span className="members-bar-pill">NEW</span>
          </NavLink>
          {aiOpen && (
            <ul className="nav-dropdown nav-dropdown-ai">
              <li>
                <NavLink to="/previews">
                  Race Previews
                  <span className="nav-dropdown-desc">nightly AI write-ups of every card</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/member/ask">
                  Ask the Notebook
                  <span className="nav-dropdown-desc">ask anything about today's racing</span>
                </NavLink>
              </li>
            </ul>
          )}
        </li>
        <li
          className="nav-dropdown-parent"
          onMouseEnter={() => setArticlesOpen(true)}
          onMouseLeave={() => setArticlesOpen(false)}
        >
          <NavLink to="/articles" title="Betting insights and strategy articles" className={location.pathname.startsWith("/articles") ? "active" : ""}>Articles</NavLink>
          {articlesOpen && (
            <ul className="nav-dropdown">
              <li><NavLink to="/articles">Articles</NavLink></li>
              <li><NavLink to="/articles/opinion">Opinion</NavLink></li>
            </ul>
          )}
        </li>
        <li
          className="nav-dropdown-parent"
          onMouseEnter={() => setMispricedOpen(true)}
          onMouseLeave={() => setMispricedOpen(false)}
        >
          <NavLink
            to="/book"
            title="Companion case studies and methodology pieces from the Mispriced book"
            className={location.pathname === "/book" ? "active" : ""}
          >
            Mispriced
          </NavLink>
          {mispricedOpen && (
            <ul className="nav-dropdown">
              <li><NavLink to="/mispriced/case-studies">Case Studies</NavLink></li>
              <li><NavLink to="/mispriced/research">Research</NavLink></li>
              <li><NavLink to="/mispriced/the-lab">The Lab</NavLink></li>
              <li><NavLink to="/mispriced/analysis">Analysis</NavLink></li>
              <li><NavLink to="/mispriced/downloads">Downloads</NavLink></li>
            </ul>
          )}
        </li>
        <li><NavLink to="/tracker" title="Follow horses, trainers & jockeys — nightly runs email">Tracker <span className="members-bar-pill">NEW</span></NavLink></li>
        <li><NavLink to="/help" title="Plain-English guides to the free tools">Help</NavLink></li>
        {/* Members dropdown removed — replaced by the dedicated <MembersBar />, which only renders for member-tier users. */}
        {/* Auth chip — sign in when logged out, email + logout when logged in.
            Hidden during initial refresh to avoid the "Sign in → Logout" flash. */}
        {!loading && (
          <li className="nav-auth">
            {user ? (
              <span className="nav-auth-account">
                <span className="nav-auth-email" title={user.email}>{user.display_name || user.email}</span>
                {/* Inline color: .nav-links a (specificity 0,1,1) overrides .nav-auth-btn (0,1,0). */}
                <NavLink to="/tracker" className="nav-auth-btn nav-auth-billing" style={{ color: "var(--cream)" }}>My Tracker</NavLink>
                {/* Billing button removed until Stripe/billing ships — /billing has no route. Re-add at paywall cutover. */}
                <button type="button" className="nav-auth-btn nav-auth-logout" onClick={handleLogout}>
                  Logout
                </button>
              </span>
            ) : (
              <NavLink to="/login" className="nav-auth-btn nav-auth-signin">Sign in</NavLink>
            )}
          </li>
        )}
      </ul>
      <button
        className={`nav-hamburger${menuOpen ? " open" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      >
        <span /><span /><span />
      </button>
      {menuOpen && (
        <div className="nav-mobile-overlay" onClick={() => setMenuOpen(false)}>
          <ul className="nav-mobile-menu" onClick={(e) => e.stopPropagation()}>
            <li>
              <button className="nav-mobile-dropdown-toggle" onClick={() => setMobileToolsOpen(!mobileToolsOpen)}>
                Cards &amp; Tools <span className={`nav-mobile-chevron${mobileToolsOpen ? " open" : ""}`}>&#9662;</span>
              </button>
              {mobileToolsOpen && (
                <ul className="nav-mobile-sub">
                  <li><NavLink to="/tools/todays-card">Today's Cards</NavLink></li>
                  <li><NavLink to="/tools/tissue">Tissue</NavLink></li>
                  <li><NavLink to="/tools/trainer">Trainers</NavLink></li>
                  <li><NavLink to="/tools/jockey">Jockeys</NavLink></li>
                  <li><NavLink to="/tools/draw-bias">Draw Bias</NavLink></li>
                  <li><NavLink to="/tools/race-radar">Race Radar</NavLink></li>
                  <li><NavLink to="/tools/calculator">Calculator</NavLink></li>
                </ul>
              )}
            </li>
            <li>
              <button className="nav-mobile-dropdown-toggle" onClick={() => setMobileAiOpen(!mobileAiOpen)}>
                AI <span className="members-bar-pill">NEW</span> <span className={`nav-mobile-chevron${mobileAiOpen ? " open" : ""}`}>&#9662;</span>
              </button>
              {mobileAiOpen && (
                <ul className="nav-mobile-sub">
                  <li><NavLink to="/previews">Race Previews</NavLink></li>
                  <li><NavLink to="/member/ask">Ask the Notebook</NavLink></li>
                </ul>
              )}
            </li>
            <li>
              <button className="nav-mobile-dropdown-toggle" onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}>
                Articles <span className={`nav-mobile-chevron${mobileArticlesOpen ? " open" : ""}`}>&#9662;</span>
              </button>
              {mobileArticlesOpen && (
                <ul className="nav-mobile-sub">
                  <li><NavLink to="/articles">Articles</NavLink></li>
                  <li><NavLink to="/articles/opinion">Opinion</NavLink></li>
                </ul>
              )}
            </li>
            <li>
              <button className="nav-mobile-dropdown-toggle" onClick={() => setMobileMispricedOpen(!mobileMispricedOpen)}>
                Mispriced <span className={`nav-mobile-chevron${mobileMispricedOpen ? " open" : ""}`}>&#9662;</span>
              </button>
              {mobileMispricedOpen && (
                <ul className="nav-mobile-sub">
                  <li><NavLink to="/book">Book Synopsis</NavLink></li>
                  <li><NavLink to="/mispriced/case-studies">Case Studies</NavLink></li>
                  <li><NavLink to="/mispriced/research">Research</NavLink></li>
                  <li><NavLink to="/mispriced/the-lab">The Lab</NavLink></li>
                  <li><NavLink to="/mispriced/analysis">Analysis</NavLink></li>
                  <li><NavLink to="/mispriced/downloads">Downloads</NavLink></li>
                </ul>
              )}
            </li>
            <li><NavLink to="/tracker">Tracker <span className="members-bar-pill">NEW</span></NavLink></li>
            <li><NavLink to="/help">Help</NavLink></li>
            {isMember && (
              <li className="nav-mobile-members">
                <div className="nav-mobile-members-header">
                  Members <span className="nav-beta-pill">BETA</span>
                </div>
                <ul className="nav-mobile-sub nav-mobile-members-flat">
                  <li><NavLink to="/member/model-picks">Model Picks</NavLink></li>
                  <li><NavLink to="/member/today-strongest">Today's Strongest</NavLink></li>
                  <li>
                    <NavLink to="/member/ask" className="nav-mobile-featured">
                      Ask the Notebook
                      <span className="members-bar-pill">NEW</span>
                    </NavLink>
                  </li>
                  <li><NavLink to="/member/tissue">Tissue</NavLink></li>
                  <li><NavLink to="/member/going-bias">Going Bias</NavLink></li>
                  <li><NavLink to="/member/backtests">Backtests</NavLink></li>
                  <li><NavLink to="/member/breeding">Breeding Stats</NavLink></li>
                  <li><NavLink to="/member/trainer-stats">Trainer Stats</NavLink></li>
                  <li><NavLink to="/member/help">Help</NavLink></li>
                </ul>
              </li>
            )}
            {/* Auth chip in mobile menu — sits at the bottom as a divider'd row. */}
            {!loading && (
              <li className="nav-mobile-auth">
                {user ? (
                  <>
                    <div className="nav-mobile-auth-email" title={user.email}>
                      Signed in as <strong>{user.display_name || user.email}</strong>
                    </div>
                    <NavLink to="/tracker" className="nav-auth-btn nav-auth-billing">My Tracker</NavLink>
                    <button
                      type="button"
                      className="nav-auth-btn nav-auth-logout"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <NavLink to="/login" className="nav-auth-btn nav-auth-signin">
                    Sign in
                  </NavLink>
                )}
              </li>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
}
